import React, { useEffect } from 'react';
import Head from 'next/head';
import setResponseHeaders from '@buzzfeed/headers';
import { useTrackingContext } from '../../hooks/useTrackingContext';
import { TrackingContext } from '../../context/TrackingContext';
import RatRaceNav from '../../components/RatRaceLanding/Nav';
import RatRaceFooter from '../../components/RatRaceLanding/Footer';
import { getRatRacePolicyBasePath } from '../../utils/ratRacePolicyBasePath';
import userAgreementImage from '../../public/static/images/rat-race/user-agreement.png';
import styles from './user-agreement.module.scss';

const PAGE_INFO = {
  context_page_type: 'utility',
  context_page_id: 'rat-race/user-agreement',
  destination: 'buzzfeed',
  page_edition: 'en-us',
};

const TIME_SPENT_INFO = {
  ...PAGE_INFO,
  unit_type: 'component',
  unit_name: 'rat_race_user_agreement',
};

const getAssetSrc = (asset) => asset?.src || asset;

function RatRaceUserAgreementContent({ policyBasePath }) {
  const { trackPageview, attachTimeSpentHandler } = useTrackingContext();

  useEffect(() => {
    trackPageview(PAGE_INFO);
    attachTimeSpentHandler(TIME_SPENT_INFO);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Head>
        <title>Rat Race | User Agreement</title>
        <meta
          name="description"
          content="RAT RACE's User Agreement"
        />
      </Head>

      <div className={styles.page}>
        <RatRaceNav />

        <main className={styles.userAgreement}>
          <header className={styles.header}>
            <h1>RAT RACE&apos;s User Agreement</h1>
            <p className={styles.effectiveDate}>Effective August 10, 2026</p>
            <img
              src={getAssetSrc(userAgreementImage)}
              alt="Rat Race User Agreement"
              className={styles.heroImage}
            />

            <div className={styles.headerContent}>
              <p className={styles.intro}>
                This User Agreement (collectively with its Privacy Policy,
                the &quot;User Agreement&quot;) applies to your use of RAT
                RACE&trade; mobile application, its associated website, and
                the services, features, and content available on the
                application or website (collectively, &quot;RAT RACE&quot;)
                provided by BuzzFeed Media Enterprises, Inc., its affiliates,
                and/or its business divisions (collectively,
                &quot;BuzzFeed&quot;, &quot;we&quot;, &quot;use&quot;, or
                &quot;our&quot;). Please read this User Agreement carefully
                as it sets forth the legally binding terms and conditions for
                your use of RAT RACE.
                <span className={styles.capsWarning}>
                  THIS USER AGREEMENT CONTAINS A CLASS ACTION WAIVER. BY
                  ACCEPTING THESE TERMS, YOU EXPRESSLY AGREE TO BE BOUND BY
                  AND ABIDE BY THIS AGREEMENT, INCLUDING THE CLASS ACTION
                  WAIVER PROVISION.
                </span>
              </p>
            </div>
          </header>

          <div className={styles.sections}>
            <section className={styles.section}>
              <h2>Acceptance of User Agreement</h2>
              <ol>
                <li>
                  By registering for and/or using RAT RACE in any manner,
                  including but not limited to visiting or browsing RAT
                  RACE, you agree to this User Agreement and all other
                  operating rules, policies and procedures that may be
                  published from time to time on RAT RACE by us, each of
                  which is incorporated by reference and each of which may
                  be updated from time to time without notice to you.
                </li>
                <li>
                  Certain RAT RACE features may be subject to additional
                  terms and conditions specified by us from time to time;
                  your use of such features is subject to those additional
                  terms and conditions, which are incorporated into this
                  User Agreement by this reference.
                </li>
              </ol>
            </section>

            <section className={styles.section}>
              <h2>Class Action Waiver</h2>
              <p>
                PLEASE REVIEW AS THIS AFFECTS YOUR LEGAL RIGHTS, INCLUDING
                YOUR RIGHT TO OBTAIN RELIEF OR DAMAGES AS A MEMBER OF A CLASS.
                {'\n\n'}
                You agree that you will not sue BuzzFeed, Inc. and any of its
                affiliates and/or business divisions as a class plaintiff or
                class representative, join as a class member, or participate
                as an adverse party in any way in a class action lawsuit
                against BuzzFeed. Nothing in this paragraph, however, limits
                your rights to bring a lawsuit as an individual plaintiff.
              </p>
            </section>

            <section className={styles.section}>
              <h2>Eligibility</h2>
              <p>
                If you are below the age of consent under applicable law in
                the country in which you reside, then your parent or legal
                guardian must read and accept the terms and conditions of
                this User Agreement in your name and on your behalf.
                {'\n'}
                In the U.S., you must be at least 16 years of age to use RAT
                RACE. By using RAT RACE, you represent and warrant that you
                are at least 16 years of age. If you are under age 16, you
                may not, under any circumstances or for any reason, use RAT
                RACE. RAT RACE is not targeted to nor meant for anyone under
                16 years of age. If you become aware of anyone using RAT RACE
                who is under the age of 16, please report this to:{' '}
                <a href="mailto:support@buzzfeed.com">
                  support@buzzfeed.com
                </a>
                . We do not knowingly collect information from anyone under
                the age of 16.
                {'\n'}
                We may, in our sole discretion, refuse to offer RAT RACE to
                any person or entity for any reason. We may also change this
                eligibility criteria at any time, in our sole discretion. You
                are solely responsible for ensuring that your use of RAT RACE
                is in compliance with all laws, rules and regulations
                applicable to you. If you are a minor, you may wish to
                consult your parents about what portions of RAT RACE are
                appropriate for you.
                {'\n'}
                Further, RAT RACE is offered only for your personal use, and
                not for any commercial use or for the use or benefit of any
                third party.
              </p>
            </section>

            <section className={styles.section}>
              <h2>Health Disclaimer</h2>
              <p>
                RAT RACE is a productivity and wellness tool that helps you
                meet your physical activity goals by:
              </p>
              <ul className={styles.bold}>
                <li>
                  Monitoring step count data from supported devices or
                  operating system health services;
                </li>
                <li>
                  Allowing users to establish daily or recurring step goals;
                </li>
                <li>
                  Temporarily restricting access to user-selected
                  applications until those goals are achieved; and
                </li>
                <li>
                  Providing related statistics, reminders, and motivational
                  features.
                </li>
              </ul>
              <p>
                RAT RACE is intended solely as a behavioral and productivity
                aid and is not a medical device or a healthcare service.
                {'\n'}
                RAT RACE does not provide medical advice, diagnosis, or
                treatment.
                {'\n'}
                BuzzFeed makes no representations that using RAT RACE will
                improve your health, fitness, weight, or medical condition.
                Always consult qualified healthcare professionals regarding
                medical concerns.
              </p>
              <p className={styles.healthWarning}>
                YOU SHOULD DISCONTINUE USE OF RAT RACE IF PHYSICAL ACTIVITY
                BECOMES UNSAFE OR INAPPROPRIATE FOR YOUR HEALTH.
              </p>
            </section>

            <section className={styles.section}>
              <h2>Emergency Disclaimer</h2>
              <p>
                RAT RACE is not intended to interfere with emergency
                communications or access to emergency services. Users should
                never configure RAT RACE in a manner that could delay
                obtaining emergency assistance.
              </p>
            </section>

            <section className={styles.section}>
              <h2>Registration</h2>
              <p>
                You may create an account on RAT RACE (an &quot;Account&quot;)
                or log in using your Apple or Google login information. To
                use certain portions of RAT RACE, you are not required to
                sign up for an Account. However, certain features of RAT RACE
                require you to register for an Account.
                {'\n\n'}
                When you register for an Account, you must provide accurate
                and complete information and keep your Account information
                updated. You shall not: (i) choose a username that is the
                name of another person, with the intent to impersonate that
                person; (ii) choose a username that is subject to any rights
                of a person other than you without appropriate authorization;
                or (iii) choose a username that is otherwise offensive,
                vulgar or obscene. You are solely responsible for all of the
                activity that occurs on your Account, and for keeping your
                Account password secure. You may never use another person&apos;s
                user account or registration information for RAT RACE
                without their permission. You must notify us immediately of
                any change in your eligibility to use RAT RACE, breach of
                security or unauthorized use of your Account. You should
                never publish, distribute or post login information for your
                Account.
                {'\n\n'}
                When using RAT RACE, certain features require your
                authorization to access:
              </p>
              <ul>
                <li>Motion and fitness activity;</li>
                <li>Step count information;</li>
                <li>
                  Health platforms such as Apple HealthKit or Android Health
                  Connect, where available;
                </li>
                <li>Notifications;</li>
                <li>
                  Accessibility or device administration permissions
                  required to temporarily restrict selected applications;
                  and
                </li>
                <li>Other permissions necessary to provide requested features.</li>
              </ul>
              <p>
                You may revoke permissions at any time through your device
                settings, although doing so may prevent portions of RAT RACE
                from functioning.
                {'\n'}
                You may control the amount of information that is accessible
                to us by adjusting your Apple or Google account privacy
                settings. By using RAT RACE, you are authorizing us to
                collect, store, retain, and use, any and all information that
                you permitted Apple or Google to provide to us in accordance
                with our Privacy Policy.
              </p>
            </section>

            <section className={styles.section}>
              <h2>App Restriction Features</h2>
              <p>
                RAT RACE allows you to voluntarily select mobile applications
                that may be temporarily restricted until you reach your
                activity goal.
              </p>
              <p>You acknowledge and agree that:</p>
              <ul>
                <li>
                  You are solely responsible for selecting restricted mobile
                  applications.
                </li>
                <li>
                  Neither RAT RACE nor BuzzFeed selects or recommends which
                  mobile applications should be restricted.
                </li>
                <li>
                  Restrictions may not function on every device, operating
                  system version, or manufacturer implementation.
                </li>
                <li>
                  Emergency communications, operating system functions, and
                  other protected device features may remain available
                  regardless of your settings.
                </li>
                <li>
                  Temporary failures, operating system updates, or
                  manufacturer limitations may affect the effectiveness of
                  app restrictions.
                </li>
                <li>
                  BuzzFeed does not guarantee uninterrupted enforcement of
                  restrictions.
                </li>
              </ul>
            </section>

            <section className={styles.section}>
              <h2>Accuracy of Step Data</h2>
              <p>
                Step counts are obtained from your device. BuzzFeed does not
                guarantee that recorded step counts are complete, accurate,
                continuous, or free from delays. Factors including device
                hardware, operating system behavior, battery-saving modes,
                wearable synchronization, and third-party platform
                limitations may affect accuracy.
                {'\n'}
                You agree that inaccuracies or synchronization delays do not
                constitute a breach of this User Agreement.
              </p>
            </section>

            <section className={styles.section}>
              <h2>Content</h2>
              <p>
                For purposes of this User Agreement, the term
                &quot;Content&quot; includes, without limitation, any
                information, data, text, photographs and other images,
                videos, audio clips, written posts, articles, comments,
                software, scripts, graphics, and interactive features
                generated, provided, or otherwise made accessible on or
                through RAT RACE. For the purposes of this Agreement,
                &quot;Content&quot; also includes all User Content (as
                defined below).
                {'\n'}
                All Content added, created, uploaded, submitted, distributed,
                or posted to RAT RACE by you or other users (collectively
                &quot;User Content&quot;), whether publicly posted or
                privately transmitted, is the sole responsibility of the
                person who originated such User Content. You represent to us
                that all User Content provided by you is yours and/or you
                have lawful permission or all rights necessary to submit,
                use, or display User Content.
                {'\n'}
                You acknowledge that all Content, including User Content,
                accessed by you using RAT RACE is at your own risk and you
                will be solely responsible for any damage or loss to you or
                any other party resulting from your actions.
                {'\n'}
                We do not guarantee that any Content you access on or through
                RAT RACE is or will continue to be accurate or available.
                {'\n\n'}
                <strong>Intellectual Property Rights.</strong>
                {'\n\n'}
                RAT RACE may contain Content specifically provided by us, our
                partners or our users, and such Content is protected by
                copyrights, trademarks, service marks, patents, trade
                secrets, or other proprietary rights and laws. You shall
                abide by all copyright or other legal notices, information,
                and restrictions contained in any Content accessed through
                RAT RACE (&quot;Protected Content&quot;). The trademarks,
                logos, trade names, trade dress, and service marks, whether
                registered or unregistered (collectively the
                &quot;Trademarks&quot;) displayed on RAT RACE are Trademarks
                of BuzzFeed and its third party partners. Nothing contained
                on RAT RACE shall be construed as granting by implication or
                otherwise, any license or right to use any Protected Content
                or Trademarks displayed on RAT RACE without the written
                permission of BuzzFeed or such third party that may own the
                Protected Content or Trademarks. Except as expressly granted
                in this User Agreement, we reserve all right, title, and
                interest in and to RAT RACE, including all copyrights,
                trademarks, service marks, trade names, logos, trade dress,
                patents, trade secrets, and other intellectual property
                rights.
                {'\n'}
                You retain ownership of your own User Content.
                {'\n'}
                Any unauthorized commercial use of the Content or Trademarks
                will violate the intellectual property rights of BuzzFeed
                and/or third parties associated with BuzzFeed and will be
                subject to BuzzFeed&apos;s and/or those third party&apos;s
                full legal rights and remedies.
                {'\n\n'}
                <strong>Use License to and from Us.</strong>
                {'\n\n'}
                Subject to this User Agreement, we grant each user of RAT
                RACE a worldwide, non-exclusive, revocable, non-sublicensable
                and non-transferable license to view, print, download, and
                display Content, to the extent we hold such rights, solely
                for your personal use of RAT RACE. Use, reproduction,
                modification, distribution or storage of any Content for any
                other purpose is expressly prohibited without prior written
                permission from us. You shall not sell, license, rent, or
                otherwise use or exploit any Content for commercial purpose
                or in any way that violates any third party rights.
                {'\n'}
                By submitting User Content through RAT RACE, you grant us a
                worldwide, non-exclusive, perpetual, royalty-free, fully
                paid, sublicensable and transferable, license to use, edit,
                modify, truncate, aggregate, reproduce, distribute, prepare
                derivative works of, display, perform, and otherwise fully
                exploit the User Content in connection with RAT RACE and our
                (and our successors&apos; and assigns&apos;) businesses,
                including without limitation for promoting and redistributing
                part or all of RAT RACE (and derivative works thereof) in any
                media formats and through any media channels (including,
                without limitation, third party websites and feeds),
                including after your termination of your Account. For the
                sake of clarity, the foregoing license grant includes our
                right to distribute, display, perform and otherwise use the
                User Content in connection with material provided by our
                advertisers and other business partners, and you shall not
                be entitled to any remuneration for such use. To the extent
                any User Content you submit includes any personal
                information, including your name, likeness, or photograph,
                you acknowledge and agree that this license shall apply to
                the same. You represent and warrant that you have all rights
                to grant such licenses to us without infringement or
                violation of any third party rights, including without
                limitation, any privacy rights, publicity rights,
                copyrights, trademarks, contract rights, or any other
                intellectual property or proprietary rights.
                {'\n\n'}
                <strong>Availability of Content.</strong>
                {'\n\n'}
                We do not guarantee that any Content will be made available
                on RAT RACE or that Content will be available uninterrupted
                or error-free. We reserve the right to, but do not have any
                obligation to, (i) monitor, remove, edit, modify or otherwise
                manipulate any Content in our sole discretion, at any time,
                without notice to you and for any reason (including, but not
                limited to, upon receipt of claims or allegations from third
                parties or authorities relating to such Content or if we are
                concerned that you may have violated this User Agreement),
                or for no reason at all and (ii) to remove or block any
                Content from RAT RACE.
              </p>
            </section>

            <section className={styles.section}>
              <h2>Rules of Conduct</h2>
              <p>
                As a condition of use, you promise not to use RAT RACE for
                any purpose that is prohibited by this User Agreement. You
                are responsible for all of your activity in connection with
                RAT RACE. You shall not (and shall not permit any third party
                to) take any action that:
              </p>
              <ul>
                <li>
                  is unlawful or is otherwise inappropriate as determined by
                  us in our sole discretion;
                </li>
                <li>attempts to bypass any security measures;</li>
                <li>
                  contains software viruses or any other computer codes,
                  files, or programs that are designed or intended to
                  disrupt, damage, limit or interfere with the proper
                  function of any software, hardware, or telecommunications
                  equipment or to damage or obtain unauthorized access to
                  any system, data, password or other information of ours or
                  of any third party;
                </li>
                <li>introduces malware or malicious code;</li>
                <li>
                  interfere or attempt to interfere with the proper working
                  of RAT RACE or any activities conducted on RAT RACE;
                </li>
                <li>misrepresents your identity;</li>
                <li>
                  bypasses or circumvents mobile app restrictions through
                  unauthorized technical means where such conduct violates
                  applicable law or platform policies;
                </li>
                <li>
                  imposes or may impose (as determined by us in our sole
                  discretion) an unreasonable or disproportionately large
                  load on our (or our third party providers&apos;)
                  infrastructure;
                </li>
                <li>
                  runs any form of auto-responder or &quot;spam&quot; on RAT
                  RACE;
                </li>
                <li>
                  deciphers, decompiles, disassembles, reverse engineers, or
                  otherwise attempts to derive any source code or underlying
                  ideas or algorithms of any part of RAT RACE; or
                </li>
                <li>otherwise is in violation of our guidelines and policies.</li>
              </ul>
              <p>
                We reserve the right to access, read, preserve, and disclose
                any information as we reasonably believe is necessary to: (i)
                satisfy any applicable law, regulation, legal process or
                governmental request; (ii) enforce this User Agreement,
                including investigation of potential violations hereof;
                (iii) detect, prevent, or otherwise address fraud, security
                or technical issues; (iv) respond to user support requests;
                or (v) protect the rights, property or safety of us, our
                users and the public.
              </p>
            </section>

            <section className={styles.section}>
              <h2>Third Party Services</h2>
              <p>
                RAT RACE may permit you to link to other products, software,
                hardware, networks, and services by third parties
                (&quot;Third-Party Services&quot;). These Third-Party
                Services may include, without limitation:
              </p>
              <ul className={styles.bold}>
                <li>Mobile operating systems, including Apple iOS and Google Android;</li>
                <li>
                  Mobile application marketplaces, including the Apple App
                  Store and Google Play;
                </li>
                <li>
                  Health and fitness platforms, including Apple HealthKit
                  and Google Health Connect;
                </li>
                <li>Wearable devices and fitness trackers;</li>
                <li>
                  Cloud hosting, storage, authentication, and notification
                  providers;
                </li>
                <li>
                  Analytics, crash reporting, customer support, and fraud
                  prevention services; and
                </li>
                <li>
                  Other third-party applications or services that you choose
                  to connect to or authorize for use with RAT RACE.
                </li>
              </ul>
              <p>
                When you access Third-Party Services, you do so at your own
                risk. Your use of any Third-Party Service is governed solely
                by the applicable third party&apos;s terms of service,
                privacy policy, and other agreements. This Agreement governs
                only your relationship with the Company. BuzzFeed is not a
                party to, and assumes no responsibility for, agreements
                between you and any third-party provider. For the avoidance
                of doubt, Third-Party Services are not under our control, and
                you acknowledge that we are not responsible or liable for the
                conduct, content, functions, accuracy, legality,
                appropriateness, or any other aspect of such services or
                resources.
                {'\n'}
                To provide certain features, RAT RACE may request
                authorization to access Third-Party Services on your behalf,
                including health data, motion activity, wearable device
                data, notifications, authentication services, or other
                information you authorize. Certain functionality requires
                accessibility permissions or equivalent operating-system
                authorization. By granting such authorization, you represent
                that you have the authority to permit the requested access.
                You may revoke these permissions at any time through your
                device settings or the applicable Third-Party Service.
                Revoking permissions may disable some or all functionality
                of the Services.
                {'\n'}
                If you believe that any linked services or resources violate
                applicable law or may be inappropriate, please notify us. We
                will review and may, in our sole discretion, remove the link
                from RAT RACE. The inclusion of any such link does not imply
                any association between us and their operators. You further
                acknowledge and agree that we shall not be responsible or
                liable, directly or indirectly, for any damage or loss caused
                or alleged to be caused by or in connection with the use of
                or reliance on any such content, goods or services available
                on or through any such Third-Party Services.
              </p>
            </section>

            <section className={styles.section}>
              <h2>Additional Terms May Apply</h2>
              <p>
                Depending on the Content or features included in RAT RACE,
                additional terms may apply (&quot;Additional Terms&quot;).
                Additional Terms may also apply for promotions, sweepstakes,
                contest, giveaways, or similar programs. If this User
                Agreement is inconsistent with any Additional Terms, the
                Additional Terms will control.
              </p>
            </section>

            <section className={styles.section}>
              <h2>Warranty Disclaimer</h2>
              <p>
                We have no special relationship with or fiduciary duty to
                you.
                {'\n\n'}
                RAT RACE IS PROVIDED &quot;AS IS&quot;, &quot;AS
                AVAILABLE,&quot; AND WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
                IMPLIED, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
                WARRANTIES OF TITLE, NON-INFRINGEMENT, MERCHANTABILITY AND
                FITNESS FOR A PARTICULAR PURPOSE, AND ANY WARRANTIES IMPLIED
                BY ANY COURSE OF PERFORMANCE OR USAGE OF TRADE, ALL OF WHICH
                ARE EXPRESSLY DISCLAIMED. WE, OUR AFFILIATES, AND OUR
                RESPECTIVE DIRECTORS, OFFICERS, EMPLOYEES, AGENTS, SUPPLIERS,
                PARTNERS, CONTRACTORS, AND CONTENT PROVIDERS (COLLECTIVELY,
                THE &quot;BUZZFEED ENTITIES&quot;) DO NOT REPRESENT OR
                WARRANT THAT: (I) RAT RACE WILL BE SECURE OR AVAILABLE AT ANY
                PARTICULAR TIME OR LOCATION; (II) ANY DEFECTS OR ERRORS WILL
                BE CORRECTED; (III) ANY CONTENT OR SOFTWARE AVAILABLE AT OR
                THROUGH THE SERVICES IS FREE OF VIRUSES OR OTHER HARMFUL
                COMPONENTS; OR (IV) THE RESULTS OF USING RAT RACE WILL MEET
                YOUR REQUIREMENTS. YOUR USE OF RAT RACE IS SOLELY AT YOUR OWN
                RISK. PLEASE NOTE THAT SOME JURISDICTIONS MAY NOT ALLOW THE
                EXCLUSION OF IMPLIED WARRANTIES, SO SOME OF THE ABOVE
                EXCLUSIONS MAY NOT APPLY TO YOU.
                {'\n\n'}
                <strong>Indemnification.</strong>
                {'\n\n'}
                You shall defend, indemnify, and hold harmless the BuzzFeed
                Entities from all liabilities, claims, and expenses,
                including reasonable attorneys&apos; fees, that arise from or
                relate to: (i) your use or misuse of, or access to, RAT RACE,
                Content, Trademarks, or otherwise from your User Content;
                (ii) any violation of this User Agreement, or any
                infringement by you of any intellectual property or other
                right of any person or entity; or (iii) any third party
                using your Account or identity in RAT RACE. We reserve the
                right to assume the exclusive defense and control of any
                matter otherwise subject to indemnification by you, in which
                event you will assist and cooperate with us in asserting any
                available defenses.
                {'\n\n'}
                <strong>Limitation of Liability.</strong>
                {'\n\n'}
                IN NO EVENT SHALL THE BUZZFEED ENTITIES BE LIABLE UNDER
                CONTRACT, TORT, STRICT LIABILITY, NEGLIGENCE OR ANY OTHER
                LEGAL OR EQUITABLE THEORY WITH RESPECT TO RAT RACE: (I) FOR
                ANY LOST PROFITS, DATA LOSS, COST OF PROCUREMENT OF
                SUBSTITUTE GOODS OR SERVICES, OR SPECIAL, INDIRECT,
                INCIDENTAL, PUNITIVE, COMPENSATORY, OR CONSEQUENTIAL DAMAGES
                OF ANY KIND WHATSOEVER, SUBSTITUTE GOODS OR SERVICES (HOWEVER
                ARISING); (II) FOR ANY BUGS, VIRUSES, TROJAN HORSES, OR THE
                LIKE (REGARDLESS OF THE SOURCE OF ORIGINATION); OR (III) FOR
                ANY DIRECT DAMAGES IN EXCESS OF (IN THE AGGREGATE) $500.00.
                APPLICABLE LAW MAY NOT ALLOW THE LIMITATION OR EXCLUSION OF
                LIABILITY OR INCIDENTAL OR CONSEQUENTIAL DAMAGES, SO THE
                ABOVE LIMITATION OR EXCLUSION MAY NOT APPLY TO YOU.
              </p>
            </section>

            <section className={styles.section}>
              <h2>Miscellaneous</h2>
              <p>
                <strong>Our Communications to You.</strong>
                {'\n\n'}
                We may communicate with you electronically, including by
                posting notices on RAT RACE or by responding to your emails.
                You agree that all agreements, notices, disclosures or other
                communications that we provide to you electronically satisfy
                any legal requirement that such communications be in
                writing.
                {'\n\n'}
                <strong>Feedback and Unsolicited Material.</strong>
                {'\n\n'}
                Any feedback, comments, or suggestions you may provide
                regarding RAT RACE is entirely voluntary and we will be free
                to use such feedback, comments or suggestions as we see fit
                and without any compensation or obligation to you.
                {'\n\n'}
                <strong>Termination.</strong>
                {'\n\n'}
                We may terminate your access to all or any part of RAT RACE
                at any time, with or without cause, with or without notice,
                effective immediately, which may result in the forfeiture
                and destruction of all information associated with your
                Account. If you wish to terminate your Account, you may do
                so by following the instructions on RAT RACE. All provisions
                of this User Agreement which by their nature should survive
                termination shall survive termination, including, without
                limitation, licenses of User Content, ownership provisions,
                warranty disclaimers, indemnity and limitations of liability.
                {'\n\n'}
                <strong>Entire Agreement and Severability.</strong>
                {'\n\n'}
                This User Agreement is the entire agreement between you and
                us with respect to RAT RACE, and supersedes all prior or
                contemporaneous communications and proposals (whether oral,
                written or electronic) between you and us with respect to
                RAT RACE. If any provision of this User Agreement is found
                to be unenforceable or invalid, that provision will be
                limited or eliminated to the minimum extent necessary so
                that this User Agreement will otherwise remain in full force
                and effect and enforceable.
                {'\n\n'}
                <strong>Import and Export Jurisdiction.</strong>
                {'\n\n'}
                Content and software from RAT RACE may be subject to U.S.
                export jurisdiction and the import jurisdiction of other
                countries. In connection with your use of RAT RACE, you are
                solely responsible for complying with all applicable export,
                re-export, and import control laws and regulations of all
                applicable jurisdictions, including, but not limited to,
                those of the U.S. Department of Commerce, Export
                Administration Regulations, 15 CFR Parts 730-774, the
                International Traffic in Arms Regulations, country-specific
                economic sanctions programs implemented by the Office of
                Foreign Assets Control and export and import control laws and
                regulations of any other countries. You may not, directly or
                indirectly, use, distribute, transfer or transmit content or
                software from RAT RACE, whether by way of a direct product or
                of such materials or products, software, or other technical
                information into which content or software from RAT RACE has
                been incorporated, except in compliance with all applicable
                export and import laws and regulations of all relevant
                jurisdictions.
                {'\n\n'}
                <strong>Force Majeure.</strong>
                {'\n\n'}
                We shall not be liable for any failure to perform our
                obligations hereunder where such failure results from any
                cause beyond our reasonable control, including, without
                limitation, mechanical, electronic or communications
                failure or degradation.
                {'\n\n'}
                <strong>Assignment.</strong>
                {'\n\n'}
                This User Agreement is personal to you, and is not
                assignable, transferable or sublicensable by you except with
                our prior written consent. We may assign, transfer or
                delegate any of our rights and obligations hereunder without
                consent.
                {'\n\n'}
                <strong>Agency.</strong>
                {'\n\n'}
                No agency, partnership, joint venture, or employment
                relationship is created as a result of this User Agreement
                and neither party has any authority of any kind to bind the
                other in any respect.
                {'\n\n'}
                <strong>Notices.</strong>
                {'\n\n'}
                Unless otherwise specified in this User Agreement,
                all notices under this User Agreement will be in writing and
                will be deemed to have been duly given when received, if
                personally delivered or sent by certified or registered
                mail, return receipt requested; when receipt is
                electronically confirmed, if transmitted by facsimile or
                e-mail; or the day after it is sent, if sent for next day
                delivery by recognized overnight delivery service. Electronic
                notices should be sent to{' '}
                <a href="mailto:info@buzzfeed.com">info@buzzfeed.com</a>.
                {'\n\n'}
                <strong>No Waiver.</strong>
                {'\n\n'}
                Our failure to enforce any part of this User Agreement shall
                not constitute a waiver of our right to later enforce that
                or any other part of this User Agreement. Waiver of
                compliance in any particular instance does not mean that we
                will waive compliance in the future. In order for any waiver
                of compliance with this User Agreement to be binding, we
                must provide you with written notice of such waiver through
                one of our authorized representatives.
                {'\n\n'}
                <strong>Governing Law and Jurisdiction.</strong>
                {'\n\n'}
                This User Agreement shall be governed by and construed in
                accordance with the laws of the State of New York, excluding
                its conflicts of law principles. You agree that any dispute
                arising from or relating to the subject matter of this User
                Agreement shall be governed by the exclusive jurisdiction and
                venue of the state and federal courts of New York County,
                New York, except where the jurisdiction and venue are
                mandated by applicable law.
                {'\n\n'}
                <strong>Modification.</strong>
                {'\n\n'}
                We reserve the right, in our sole discretion, to modify or
                replace any part of this User Agreement, or change, suspend,
                or discontinue RAT RACE (including without limitation, the
                availability of any feature, database, or content) at any
                time by posting a notice on RAT RACE or by sending you notice
                through RAT RACE, via e-mail or by another appropriate means
                of electronic communication. We may also impose limits on
                certain features and services or restrict your access to
                parts or all of RAT RACE without notice or liability. While
                we will timely provide notice of modifications, it is also
                your responsibility to check this User Agreement
                periodically for changes. Your continued use of RAT RACE
                following notification of any changes to this User Agreement
                constitutes acceptance of those changes.
                {'\n\n'}
                <strong>Headings.</strong>
                {'\n\n'}
                The section and paragraph headings in this User Agreement
                are for convenience only and shall not affect their
                interpretation.
                {'\n\n'}
                <strong>Contact and Company details.</strong>
                {'\n\n'}
                BuzzFeed, Inc. is a Delaware corporation with its
                headquarters at 50 West 23rd Street, 6th Floor, New York, NY
                10010. You may contact us at the following address: 50 West
                23rd Street, 6th Floor, New York, NY 10010, or at the
                following email address:{' '}
                <a href="mailto:support@buzzfeed.com">
                  support@buzzfeed.com
                </a>
                .
              </p>
            </section>
          </div>
        </main>

        <RatRaceFooter policyBasePath={policyBasePath} />
      </div>
    </>
  );
}

export async function getServerSideProps({ req, res }) {
  setResponseHeaders({
    req,
    res,
    maxAge: 86400,
    contentIDs: [{ type: 'quiz', id: 'rat-race-user-agreement' }],
  });

  const policyBasePath = getRatRacePolicyBasePath(req);

  return { props: { policyBasePath } };
}

export default function RatRaceUserAgreementPage({ policyBasePath }) {
  return (
    <TrackingContext.Provider value={PAGE_INFO}>
      <RatRaceUserAgreementContent policyBasePath={policyBasePath} />
    </TrackingContext.Provider>
  );
}
